import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, Grid, Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { useSnackbar } from 'notistack';
import { HeaderTable, InfoTable, useOrderByState, useRouteScreenTitle } from 'src/commons';
import { Publication } from 'src/app/models/business/Publication';
import { createPublication } from './api/createPublication';
import { importPrices } from './api/importPrices';
import { updatePublication } from './api/updatePublication';
import {
  mapFormToCreatePayload,
  mapFormToEditPayload,
} from './api/mapPublicationFormToPayload';
import {
  PublicationsHeader,
  PublicationFormModal,
  ImportPricesModal,
  PublicationLogsModal,
  OnePaymentModal,
  PublicationFormValues,
} from './components';
import { isPvpImportRow } from './components/ImportPricesModal/parseImportPricesCsv';
import { ImportPriceRow } from './components/ImportPricesModal/types';
import { PublicationFilters } from './components/PublicationsHeader/types';
import { usePublicationsData } from './hooks/useData';
import { usePublicationColumns } from './hooks/usePublicationColumns';
import { usePublicationZones } from './hooks/usePublicationZones';

const useStyles = makeStyles({
  typography: {
    fontSize: 12,
  },
  filtersSection: {
    width: '100%',
  },
  topActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginBottom: 16,
  },
  headerActions: {
    display: 'flex',
    gap: 10,
  },
  actionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    border: '0.5px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    background: '#f8f7f6',
    color: '#6b6a66',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    padding: '4px 13px',
    transition: 'all .15s',
    whiteSpace: 'nowrap',
  },
  actionButtonPrimary: {
    background: '#E8336A',
    color: '#fff',
    fontWeight: 500,
    border: 'none',
  },
  marketplaceSwitch: {
    display: 'flex',
    overflow: 'hidden',
    border: '0.5px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    background: '#f8f7f6',
  },
  marketplaceButton: {
    border: 'none',
    background: 'transparent',
    color: '#6b6a66',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    padding: '4px 13px',
    transition: 'all .15s',
  },
  marketplaceButtonActive: {
    background: '#E8336A',
    color: '#fff',
    fontWeight: 500,
  },
});

const MARKETPLACES = [
  { value: 'pipestore', label: 'PipeStore' },
  { value: 'enova', label: 'Enova' },
];

export const Publications = () => {
  const {
    data,
    loading,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onFilter,
    onOrderByChange,
    reLoad,
  } = usePublicationsData();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  useRouteScreenTitle('Publicaciones');

  const [filter, setFilter] = useState<PublicationFilters>({
    search: '',
    publication_state_id: '1',
    marketplace: 'pipestore',
    zoneIds: [],
    orderBy: '',
    direction: '',
  });

  const { direction, orderBy, onOrderByDirectionChange } = useOrderByState();
  const { zoneOptions, loadingZones } = usePublicationZones({ marketplace: filter.marketplace });

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPublication, setEditingPublication] = useState<Publication | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [onePaymentModalOpen, setOnePaymentModalOpen] = useState(false);
  const [logsPublicationId, setLogsPublicationId] = useState<string | null>(null);

  const handleCreate = useCallback(() => {
    setEditingPublication(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((publication: Publication) => {
    setEditingPublication(publication);
    setFormModalOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormModalOpen(false);
    setEditingPublication(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (values: PublicationFormValues, isEditing: boolean, paymentCards = []) => {
      const marketplace = filter.marketplace as 'pipestore' | 'enova';

      try {
        if (isEditing) {
          await updatePublication(mapFormToEditPayload(values, marketplace, paymentCards));
          enqueueSnackbar('Publicación actualizada correctamente', { variant: 'success' });
        } else {
          await createPublication(mapFormToCreatePayload(values, marketplace, paymentCards));
          enqueueSnackbar('Publicación creada correctamente', { variant: 'success' });
        }
        reLoad();
      } catch (error: any) {
        enqueueSnackbar(error.response?.data || 'Error al guardar la publicación', {
          variant: 'error',
        });
        throw error;
      }
    },
    [filter.marketplace, enqueueSnackbar, reLoad],
  );

  const handleConfirmImport = useCallback(
    async (rows: ImportPriceRow[]) => {
      const payload = rows
        .filter(isPvpImportRow)
        .map(({ publication_id, precio_pvp, precio_pv }) => ({
          publication_id,
          precio_pvp,
          precio_pv,
        }));

      try {
        await importPrices(payload);
        enqueueSnackbar('Precios importados correctamente', { variant: 'success' });
        reLoad();
      } catch (error: any) {
        enqueueSnackbar(error.response?.data || 'Error al importar precios', {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, reLoad],
  );

  const handleViewLogs = useCallback((publication: Publication) => {
    setLogsPublicationId(publication.publication_id ?? null);
    setLogsModalOpen(true);
  }, []);

  const handleMarketplaceChange = useCallback((marketplace: string) => {
    setFilter((oldValue) => ({
      ...oldValue,
      marketplace,
      zoneIds: [],
    }));
  }, []);

  const handleCloseLogs = useCallback(() => {
    setLogsModalOpen(false);
    setLogsPublicationId(null);
  }, []);

  const columns = usePublicationColumns({ onEdit: handleEdit, onViewLogs: handleViewLogs });

  useEffect(() => {
    onFilter(filter);
  }, [filter, onFilter]);

  useEffect(() => {
    onOrderByChange(orderBy, direction);
  }, [orderBy, direction, onOrderByChange]);

  return (
    <Grid container>
      <Box p={2} width="100%">
        <HeaderTable
          title=""
          renderLeft={() => null}
          renderRight={() => (
            <div className={classes.topActions}>
              <div className={classes.marketplaceSwitch}>
                {MARKETPLACES.map((marketplace) => (
                  <button
                    key={marketplace.value}
                    type="button"
                    className={[
                      classes.marketplaceButton,
                      filter.marketplace === marketplace.value ? classes.marketplaceButtonActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleMarketplaceChange(marketplace.value)}
                  >
                    {marketplace.label}
                  </button>
                ))}
              </div>
              <div className={classes.headerActions}>
                <button
                  type="button"
                  className={classes.actionButton}
                  onClick={() => setOnePaymentModalOpen(true)}
                >
                  <CreditCardIcon style={{ fontSize: 16 }} />
                  1 Pago
                </button>
                <button
                  type="button"
                  className={classes.actionButton}
                  onClick={() => setImportModalOpen(true)}
                >
                  <ImportExportIcon style={{ fontSize: 16 }} />
                  Importar PVP/PV
                </button>
                <button
                  type="button"
                  className={`${classes.actionButton} ${classes.actionButtonPrimary}`}
                  onClick={handleCreate}
                >
                  <AddIcon style={{ fontSize: 16 }} />
                  Nueva publicación
                </button>
              </div>
            </div>
          )}
        >
          <Box className={classes.filtersSection}>
            <PublicationsHeader
              filter={filter}
              onChangeFilter={setFilter}
              publications={data}
              totalCount={totalCount}
              zoneOptions={zoneOptions}
              loadingZones={loadingZones}
            />
          </Box>
        </HeaderTable>
        <HeaderTable title="Publicaciones" />
        <InfoTable
          working={loading}
          rows={data}
          columns={columns}
          rowIdKey="publication_id"
          customClassnameTypography={classes.typography}
          pagination={{
            page,
            rowsPerPage,
            count: totalCount,
            onPageChange,
          }}
          direction={direction}
          orderBy={orderBy}
          onSortChange={onOrderByDirectionChange}
          headerPadding="5px 10px"
        />
      </Box>

      <PublicationFormModal
        open={formModalOpen}
        marketplace={filter.marketplace as 'pipestore' | 'enova'}
        publication={editingPublication}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
      />

      <ImportPricesModal
        open={importModalOpen}
        variant="pvp"
        onClose={() => setImportModalOpen(false)}
        onConfirm={handleConfirmImport}
      />

      <OnePaymentModal
        open={onePaymentModalOpen}
        onClose={() => setOnePaymentModalOpen(false)}
        onApplied={reLoad}
      />

      <PublicationLogsModal
        open={logsModalOpen}
        publicationId={logsPublicationId}
        onClose={handleCloseLogs}
      />
    </Grid>
  );
};

export const PublicationsView = Publications;
